import { getConfig } from '@/configs/config-accessor';
import { REQUEST_HEADER_AUTH_KEY } from '@/constants/api.constant';
import axios from 'axios';
import { OcrSpaceOptions, OcrSpaceResponse } from 'ocr-space-api-wrapper';
import { CreateChatCompletionRequest, CreateChatCompletionResponse } from 'openai';
import ApiService from './ApiService';
import { handleSetApiAuthorizationHeader } from './BaseService';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

// Helper function to append company name to baseUrl if needed
const getBaseUrlWithCompany = (baseUrl?: string) => {
  if (!baseUrl) return baseUrl;

  const storedCompanyName = localStorage.getItem('companyName');
  const config = getConfig();

  if (storedCompanyName && config.companyLogin) {
    try {
      const companyName = atob(storedCompanyName);
      return `${baseUrl}/${companyName}`;
    } catch (e) {
      console.warn('Failed to decode stored company name');
      return baseUrl;
    }
  }

  return baseUrl;
};

export type GetDynamicApi_Response<T = Record<string, unknown>> = T[];
export const GetDynamicApi = async <T = Record<string, unknown>>(query: DynamicGet_Body, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<GetDynamicApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamicmongodb/get` : `${newApiPrefix}/dynamicmongodb/get`,
    method: 'post',
    data: {
      collection: query.collection,
      distinct: query.distinct,
      filter: query.filter,
      limit: query.limit,
      select: query.select,
      skip: query.skip,
      sort: query.sort,
    },
  });
};

export const GetDynamicDataSourceApi = async <T = Record<string, unknown>>(query: DynamicGet_Body, baseUrl?: string) => {
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<GetDynamicApi_Response<T>>({
    url: `${finalBaseUrl}`,
    method: 'post',
    data: {
      collection: query.collection,
      distinct: query.distinct,
      filter: query.filter,
      limit: query.limit,
      select: query.select,
      skip: query.skip,
      sort: query.sort,
    },
  });
};

export type GetDynamicQueryApi_Response<T = any> = IResponse<T | null>;
export const GetDynamicQueryApi = async <T = any>(slug: string, parameters?: string, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<GetDynamicQueryApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamic/query/${slug}${parameters ? `${parameters}` : ``}` : `${newApiPrefix}/dynamic/query/${slug}${parameters ? `/${parameters}` : ``}`,
    method: 'post',
  });
};

export type GetFormSchemaApi_Response = IResponse<any[] | null>;
export const GetFormSchemaApi = async (formId: string, baseUrl?: string) => {
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return GetDynamicApi(
    {
      collection: 'form-builder',
      filter: { _id: formId },
    },
    finalBaseUrl
  );
};

export const GetDynamicCustomQueryApi = async <T = any>(slug: string, parameters?: string, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<GetDynamicQueryApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamic/query/custom${slug}${parameters ? `${parameters}` : ``}` : `${newApiPrefix}/dynamic/query/${slug}${parameters ? `/${parameters}` : ``}`,
    method: 'post',
  });
};

export type GetDynamicWithHeaderApiApi_Response = IResponse<Record<string, unknown>[] | null>;
export const GetDynamicWithHeaderApi = async (query: DynamicGet_Body, headers: { token: string }) => {
  const newApiPrefix = getApiPrefix();
  return axios.post(
    `${newApiPrefix}/dynamicmongodb/get`,
    {
      collection: query.collection,
      filter: query.filter,
      limit: query.limit,
      skip: query.skip,
      sort: query.sort,
      select: query.select,
    },
    {
      headers: {
        [REQUEST_HEADER_AUTH_KEY]: handleSetApiAuthorizationHeader(headers.token),
      },
    }
  );
};

export type CreateDynamicApi_Response<T = Record<string, unknown>> = IResponse<T | null>;
export const CreateDynamicDataSourceApi = async <T = Record<string, unknown>>(query: DynamicCreate_Body<unknown>, baseUrl?: string) => {
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<CreateDynamicApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}` : `${getApiPrefix()}/dynamicmongodb/insert`,
    method: 'post',
    data: {
      collection: query.collection,
      data: query.data,
    },
  });
};

export const CreateDynamicApi = async <T = Record<string, unknown>>(query: DynamicCreate_Body<unknown>, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<CreateDynamicApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamicmongodb/insert` : `${newApiPrefix}/dynamicmongodb/insert`,
    method: 'post',
    data: {
      collection: query.collection,
      data: query.data,
    },
  });
};

export type CreateManyDynamicApi_Response<T = Record<string, unknown>> = IResponse<T | null>;
export const CreateManyDynamicApi = async <T = Record<string, unknown>>(query: DynamicCreate_Body<unknown>) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<CreateDynamicApi_Response<T>>({
    url: `${newApiPrefix}/dynamic/create-many`,
    method: 'post',
    data: {
      collection: query.collection,
      data: query.data,
    },
  });
};

export type updateDynamicApi_Response<T = Text> = IResponse<T[] | null>;
export const UpdateDynamicApi = async <T = Text>(query: DynamicUpdate_Body<unknown>, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<updateDynamicApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamicmongodb/update` : `${newApiPrefix}/dynamicmongodb/update`,
    method: baseUrl ? 'post' : 'post',
    data: {
      collection: query.collection,
      filter: query.filter,
      data: query.data,
    },
  });
};

export const UpdateDynamicDataSourceApi = async <T = Text>(query: DynamicUpdate_Body<unknown>, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<updateDynamicApi_Response<T>>({
    url: finalBaseUrl ? `${finalBaseUrl}` : `${newApiPrefix}/dynamicmongodb/update`,
    method: baseUrl ? 'post' : 'post',
    data: {
      collection: query.collection,
      filter: query.filter,
      data: query.data,
    },
  });
};

export type DeleteDynamicApi_Response<T = Record<string, unknown>> = IResponse<T[] | null>;
export const DeleteDynamicApi = async <T = Record<string, unknown>>(query: DynamicDelete_Body, baseUrl?: string) => {
  const newApiPrefix = getApiPrefix();
  const finalBaseUrl = getBaseUrlWithCompany(baseUrl);
  return ApiService.fetchData<DeleteDynamicApi_Response<T>, DynamicDelete_Body>({
    url: finalBaseUrl ? `${finalBaseUrl}/dynamicmongodb/delete` : `${newApiPrefix}/dynamicmongodb/delete`,
    method: 'post',
    data: {
      collection: query.collection,
      filter: query.filter,
    },
  });
};

export type GenerateAiDynamicApi_Body = DynamicAI_Body<Omit<CreateChatCompletionRequest, 'model'>>;
export type GenerateAiDynamicApi_Response = IResponse<CreateChatCompletionResponse | null>;
export const GenerateAiDynamicApi = async (body: GenerateAiDynamicApi_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GenerateAiDynamicApi_Response, GenerateAiDynamicApi_Body>({
    url: `${newApiPrefix}/dynamic/ai`,
    method: 'post',
    data: { ...body },
  });
};

export type GenerateOCRDynamicApi_Body = DynamicOCR_Body<Omit<OcrSpaceOptions, 'apiKey'>>;
export type GenerateOCRDynamicApi_Response = IResponse<OcrSpaceResponse | null>;
export const GenerateOcrDynamicApi = async (body: GenerateOCRDynamicApi_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GenerateOCRDynamicApi_Response, GenerateOCRDynamicApi_Body>({
    url: `${newApiPrefix}/dynamic/ocr`,
    method: 'post',
    data: { ...body },
  });
};
